export type CanonicalPlanId = "starter" | "growth" | "pro" | "custom";
export type PlanBillingCycle = "monthly" | "annual";

type PlanFeatures = Record<string, boolean>;

export interface PlanCatalogEntry {
  id: CanonicalPlanId;
  name: string;
  monthly_price: number | null;
  annual_price: number | null;
  max_vehicles: number | null;
  max_users: number | null;
  max_branches: number | null;
  features: PlanFeatures;
  paddle_price_id: string | null;
  paddle_annual_price_id: string | null;
}

type PlanPriceSource = {
  id: string;
  paddle_price_id?: string | null;
  paddle_annual_price_id?: string | null;
};

type PlanLabelInput =
  | string
  | null
  | undefined
  | {
      id?: string | null;
      name?: string | null;
    };

const FULL_FEATURE_SET: PlanFeatures = {
  whatsapp: true,
  crm_integrations: true,
  data_export: true,
  push_notifications: true,
  advanced_reports: true,
  api_access: true,
  custom_branding: true,
  inspections: true,
  multi_location: true,
  public_portal: true,
  loyalty: true,
  addons_catalog: true,
  online_payment: true,
  leasing: true,
  delivery_service: true,
  insurance_claims: true,
  email_notifications: true,
  vehicle_compare: true,
};

export const PLAN_ORDER: CanonicalPlanId[] = ["starter", "growth", "pro", "custom"];

export const DEMO_PLAN_CATALOG: PlanCatalogEntry[] = [
  {
    id: "starter",
    name: "Launch",
    monthly_price: 39,
    annual_price: 390,
    max_vehicles: 15,
    max_users: 2,
    max_branches: 1,
    features: {
      data_export: true,
      inspections: true,
    },
    paddle_price_id: "pri_01knhz1mecfwwhth1ltq683ggy",
    paddle_annual_price_id: "pri_01knhz6km7fg3d58re53fwy2hd",
  },
  {
    id: "growth",
    name: "Grow",
    monthly_price: 99,
    annual_price: 990,
    max_vehicles: 40,
    max_users: 5,
    max_branches: 1,
    features: {
      whatsapp: true,
      crm_integrations: true,
      data_export: true,
      push_notifications: true,
      advanced_reports: true,
      inspections: true,
    },
    paddle_price_id: "pri_01knhz3bp4zywqm6sf220f8zfr",
    paddle_annual_price_id: "pri_01knhz9ph8w998sswghat17vne",
  },
  {
    id: "pro",
    name: "Scale",
    monthly_price: 249,
    annual_price: 2490,
    max_vehicles: 80,
    max_users: 15,
    max_branches: 10,
    features: {
      whatsapp: true,
      crm_integrations: true,
      data_export: true,
      push_notifications: true,
      advanced_reports: true,
      api_access: true,
      custom_branding: true,
      inspections: true,
      multi_location: true,
    },
    paddle_price_id: "pri_01knhz4hhwdvtpa19b9x4reexk",
    paddle_annual_price_id: "pri_01knhzcd7z0zqrmysmfvdhhz6p",
  },
  {
    id: "custom",
    name: "Custom",
    monthly_price: null,
    annual_price: null,
    max_vehicles: 999,
    max_users: 999,
    max_branches: 999,
    features: FULL_FEATURE_SET,
    paddle_price_id: null,
    paddle_annual_price_id: null,
  },
];

const DEMO_PLAN_CATALOG_BY_ID = Object.fromEntries(
  DEMO_PLAN_CATALOG.map((plan) => [plan.id, plan]),
) as Record<CanonicalPlanId, PlanCatalogEntry>;

const LEGACY_PLAN_ID_MAP: Record<string, CanonicalPlanId> = {
  launch: "starter",
  grow: "growth",
  scale: "pro",
};

export function normalizePlanId(planId: string | null | undefined): CanonicalPlanId {
  const normalized = planId ? LEGACY_PLAN_ID_MAP[planId] ?? planId : "starter";
  return normalized in DEMO_PLAN_CATALOG_BY_ID
    ? (normalized as CanonicalPlanId)
    : "starter";
}

export function getDemoPlanCatalog(): PlanCatalogEntry[] {
  return DEMO_PLAN_CATALOG;
}

export function getDemoPlanById(planId: string | null | undefined): PlanCatalogEntry {
  return DEMO_PLAN_CATALOG_BY_ID[normalizePlanId(planId)];
}

export function tryGetDemoPlanById(planId: string | null | undefined): PlanCatalogEntry | null {
  if (!planId) return null;

  const normalized = LEGACY_PLAN_ID_MAP[planId] ?? planId;
  return normalized in DEMO_PLAN_CATALOG_BY_ID
    ? DEMO_PLAN_CATALOG_BY_ID[normalized as CanonicalPlanId]
    : null;
}

export function getCatalogPlanPriceId(
  planId: string | null | undefined,
  billingCycle: PlanBillingCycle,
): string | null {
  const plan = tryGetDemoPlanById(planId);
  if (!plan) return null;

  return billingCycle === "annual" ? plan.paddle_annual_price_id : plan.paddle_price_id;
}

export function resolvePlanCheckoutPriceId(
  plan: PlanPriceSource,
  billingCycle: PlanBillingCycle,
): string | null {
  const catalogPriceId = getCatalogPlanPriceId(plan.id, billingCycle);
  const dbPriceId =
    billingCycle === "annual" ? plan.paddle_annual_price_id : plan.paddle_price_id;

  return catalogPriceId ?? dbPriceId ?? null;
}

export function getCustomerPlanLabel(plan: PlanLabelInput): string {
  if (typeof plan === "object" && plan?.name?.trim()) {
    return plan.name.trim();
  }

  const planId = typeof plan === "string" ? plan : plan?.id;
  if (!planId) return "Unknown plan";

  return getDemoPlanById(planId).name;
}

export function getAdminPlanLabel(plan: PlanLabelInput): string {
  const planId = typeof plan === "string" ? plan : plan?.id;
  if (!planId) return getCustomerPlanLabel(plan);

  return `${getCustomerPlanLabel(plan)} (${normalizePlanId(planId)})`;
}

export function formatUpgradePlanMessage(featureName: string, minimumPlanId: CanonicalPlanId): string {
  return `${featureName} is available on the ${getCustomerPlanLabel(minimumPlanId)} plan and above.`;
}

export function isCustomPlanId(planId: string | null | undefined): boolean {
  return normalizePlanId(planId) === "custom";
}

export function sortPlansByCatalogOrder<T extends { id: string }>(plans: T[]): T[] {
  const order = new Map(PLAN_ORDER.map((id, index) => [id, index]));
  return [...plans].sort((a, b) => {
    const aIndex = order.get(normalizePlanId(a.id)) ?? Number.MAX_SAFE_INTEGER;
    const bIndex = order.get(normalizePlanId(b.id)) ?? Number.MAX_SAFE_INTEGER;
    return aIndex - bIndex;
  });
}
